import { t } from 'i18next';
import type { StockLocationsAPIResponse } from '../../hooks/use-stock-locations';
import { formatMoney } from '../../utils/format-money';
import type { PriceList } from '../price-lists';
import { LIST_DELIMITER } from './constants';
import type {
  SettingAddressManagementState,
  SettingMultiStockState,
  SettingOrderQuantityLimitsValidated,
  SettingOrderTotalLimitsValidated,
  SettingPaymentMethodsState,
  SettingPriceListState,
  SettingStockDisplayValidated,
} from './types/customer-group.schema';
import type { SettingResponseItem } from './types/customer-group-response';

export function getSelectedPriceLists(setting: SettingPriceListState, priceLists: PriceList[]) {
  return setting.flatMap((s) => {
    const match = priceLists.find((p) => p.slug === s.price_list_slug);
    return match ? [match] : [];
  });
}
// only displays formatting settings if settings are set
export function displaySettings(
  setting: SettingResponseItem,
  priceLists?: PriceList[],
  stockLocations?: StockLocationsAPIResponse[],
  allowDefaultBillingModification?: boolean,
  isQuotingEngineEnabled?: boolean,
  isMultiStockEnabled?: boolean,
): string | undefined {
  if (setting.value === null || setting.value === undefined) {
    return undefined;
  }

  switch (setting.key) {
    case 'priceListSelection':
      return formatPriceLists(setting.value, priceLists ?? []);
    // this formats both the address and billing settings, as they are shown in one card
    case 'allowAddressEditing':
      return formatAddressManagement({
        allowAddressEditing: setting.value,
        allowDefaultBillingModification: allowDefaultBillingModification ?? false,
      });
    case 'allowedPaymentMethods':
      return formatPaymentMethods(setting.value);
    case 'allowCheckout':
      return t(`features:customer_group.common.checkout.${setting.value ? 'enabled' : 'disabled'}`);
    case 'multiLocationStockConfig':
      if (!isMultiStockEnabled) {
        return t('features:customer_group.common.stock_location.features_not_enabled');
      }
      return formatStockLocation(setting.value, stockLocations ?? []);
    case 'orderItemQuantityValidation':
      return formatOrderQuantityLimit(setting.value);
    case 'orderTotalsValidation':
      return formatOrderTotalLimits(setting.value);
    case 'quotingEngine':
      if (!isQuotingEngineEnabled) {
        return t('features:customer_group.common.quoting_engine.features_not_enabled');
      }
      return t(
        `features:customer_group.common.quoting_engine.${setting.value.customer_create_quote ? 'enabled' : 'disabled'}`,
      );
    case 'paymentOnAccountEnforceCreditLimit':
      return setting.value
        ? t('features:customer_group.common.credit_settings.blocked')
        : t('features:customer_group.common.credit_settings.allowed');
    case 'stockDisplay':
      return formatStockDisplay(setting.value);
  }

  return undefined;
}

function formatPriceLists(setting: SettingPriceListState, priceLists: PriceList[]): string {
  const selectedPriceLists = getSelectedPriceLists(setting, priceLists);
  if (!selectedPriceLists[0] || selectedPriceLists.length === 0) {
    return '';
  }

  const currency_code = selectedPriceLists[0].currency_code;

  const names: string = selectedPriceLists.map((list) => list.name).join(LIST_DELIMITER);

  return `${currency_code} - ${names}`;
}

function formatPaymentMethods(setting: SettingPaymentMethodsState): string {
  const value = Object.entries(setting)
    .filter(([, value]) => value !== false)
    .map(([key]) => t(`badges:payment_methods.${key as keyof SettingPaymentMethodsState}`))
    .join(LIST_DELIMITER);

  if (!value) {
    return t('labels:none');
  }

  return value;
}

function formatStockLocation(
  setting: SettingMultiStockState,
  stockLocations: StockLocationsAPIResponse[],
): string {
  if (setting.useDefault) {
    return t('features:customer_group.common.stock_location.all_locations');
  }

  const stockLocationName = stockLocations.find(
    (location) => location.id === setting.allowedStockLocationIds[0],
  )?.name;

  if (stockLocationName) {
    return t('features:customer_group.common.stock_location.fulfillment_from', {
      location: stockLocationName,
    });
  }

  return t('features:customer_group.common.stock_location.not_found');
}

function formatOrderQuantityLimit(setting: SettingOrderQuantityLimitsValidated): string {
  if (!setting.minimum_quantity && !setting.maximum_quantity) {
    return t('features:customer_group.common.order_quantity.not_set');
  }

  if (!setting.maximum_quantity) {
    return t('features:customer_group.common.order_quantity.minimum_only', {
      min: setting.minimum_quantity || '1',
    });
  }

  return t('features:customer_group.common.order_quantity.range', {
    min: setting?.minimum_quantity || '1',
    max: setting.maximum_quantity,
  });
}

function formatOrderTotalLimits(setting: SettingOrderTotalLimitsValidated): string {
  if (setting.length === 0) {
    return t('features:customer_group.common.order_total.not_set');
  }

  return setting
    .map((limit) => {
      if (!limit.maximum_value) {
        return t('features:customer_group.common.order_total.minimum_only', {
          min: formatMoney(limit.minimum_value ?? '0', limit.currency_code),
        });
      }
      return t('features:customer_group.common.order_total.range', {
        min: formatMoney(limit.minimum_value ?? '0', limit.currency_code),
        max: formatMoney(limit.maximum_value, limit.currency_code),
      });
    })
    .join(LIST_DELIMITER);
}

function formatAddressManagement(setting: SettingAddressManagementState): string {
  if (setting.allowAddressEditing && setting.allowDefaultBillingModification) {
    return t('features:customer_group.common.address_editing.both_on');
  }

  if (setting.allowAddressEditing) {
    return t('features:customer_group.common.address_editing.address');
  }

  return t('features:customer_group.common.address_editing.both_off');
}

function formatNumber(number: number | undefined) {
  return number !== undefined ? number.toString() : '';
}

function formatStockDisplay(setting: SettingStockDisplayValidated): string {
  return t('features:customer_group.common.stock_display', {
    availability: setting.show_status
      ? t('features:customer_group.common.on')
      : t('features:customer_group.common.off'),
    pre_order: setting.hide_preorder
      ? t('features:customer_group.common.off')
      : t('features:customer_group.common.on'),
    units: setting.show_units
      ? t('features:customer_group.common.on')
      : t('features:customer_group.common.off'),
    max: formatNumber(setting.max),
    low: formatNumber(setting.low),
    last: formatNumber(setting.last),
  });
}
